import { Seo } from "@/seo/Seo";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { DetailHero } from "@/components/detail/DetailHero";
import { PillList } from "@/components/detail/PillList";
import { FeatureGrid, BenefitGrid } from "@/components/detail/FeatureGrid";
import { WorkflowTimeline } from "@/components/detail/WorkflowTimeline";
import { TechStackChips } from "@/components/detail/TechStackChips";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { CaseStudyPreview } from "@/components/detail/CaseStudyPreview";
import { RelatedLinks, type RelatedLink } from "@/components/detail/RelatedLinks";
import { DetailCta } from "@/components/detail/DetailCta";
import { serviceDetails } from "@/data/serviceDetails";
import { industries } from "@/data/industries.data";
import { whyChooseUs } from "@/data/faq.data";
import type { ServiceDetail } from "@/types/detail";

function relatedServiceLinks(slugs: string[]): RelatedLink[] {
  return slugs
    .map((slug) => serviceDetails.find((s) => s.slug === slug))
    .filter((s): s is ServiceDetail => Boolean(s))
    .map((s) => ({
      label: s.title,
      description: s.category,
      to: `/services/${s.slug}`,
      icon: s.icon,
    }));
}

function relatedIndustryLinks(ids: string[]): RelatedLink[] {
  return ids
    .map((id) => industries.find((i) => i.id === id))
    .filter((i): i is (typeof industries)[number] => Boolean(i))
    .map((i) => ({ label: i.name, to: "/industries", icon: i.icon }));
}

export function ServiceDetailTemplate({ data }: { data: ServiceDetail }) {
  const titleWords = data.title.split(" ");
  const titleLines =
    titleWords.length > 2
      ? [titleWords.slice(0, -1).join(" "), titleWords.slice(-1).join(" ")]
      : [data.title];

  return (
    <>
      <Seo
        title={data.title}
        description={data.tagline}
        path={`/services/${data.slug}`}
      />
      <DetailHero
        category={`Services · ${data.category}`}
        titleLines={titleLines}
        tagline={data.tagline}
        icon={data.icon}
        accent={data.accent}
        image={data.image}
      />

      {/* Overview + ideal customers */}
      <Section spacing="compact">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.3fr_0.7fr] lg:gap-16">
            <div>
              <SectionHeading 
                eyebrow="Overview" 
                title="Understanding the solution"
                // description="Learn how this solution addresses your business requirements and supports long-term growth."
              />
              <Reveal>
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                  {data.overview}
                </p>
              </Reveal>
            </div>
            <div className="lg:pt-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Ideal for
              </h3>
              <div className="mt-5">
                <PillList items={data.idealCustomers} columns={1} />
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Problems solved */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="Problems we solve"
            title="Common business challenges"
            description="Discover the operational, technical and business challenges this solution is designed to eliminate."
          />
          <div className="mt-10">
            <PillList items={data.problems} />
          </div>
        </Container>
      </Section>

      {/* Benefits */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading 
            eyebrow="Benefits" 
            title="Outcomes for your business"
            // description="See how this solution improves productivity, efficiency, customer experience and business performance."
          />
          <div className="mt-10">
            <BenefitGrid benefits={data.benefits} />
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="Capabilities"
            title="What the solution includes"
            description="Explore the key features and capabilities that make this solution practical, scalable and easy to use."
          />
          <div className="mt-12">
            <FeatureGrid features={data.features} />
          </div>
        </Container>
      </Section>

      {/* Tech stack */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Technology"
            title="Recommended Technologies"
            description="We select proven technologies based on your business goals, scalability, security and long-term maintainability—not trends."
          />
          <div className="mt-10">
            <TechStackChips items={data.techStack} />
          </div>
        </Container>
      </Section>

      {/* Development process */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="How we work"
            title="Our implementation approach"
            description="A structured, transparent delivery process that keeps your project on schedule, within budget and aligned with your objectives."
          />
          <div className="mt-10">
            <WorkflowTimeline nodes={data.workflow} accent={data.accent} />
          </div>
        </Container>
      </Section>

      {/* Why VibeWell */}
      <Section spacing="compact">
        <Container>
          <SectionHeading 
            eyebrow="Why VibeWell" 
            title="A partner, not a vendor"
            description="We combine business understanding, engineering expertise and AI-driven innovation to deliver solutions that create measurable value."
          />
          <div className="mt-10">
            <BenefitGrid benefits={whyChooseUs} />
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions"
            align="center"
          />
          <div className="mt-10">
            <FaqAccordion faqs={data.faqs} />
          </div>
        </Container>
      </Section>

      {/* Case study */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Example Implementation"
            title="Typical Business Solution"
            description="An illustrative example showing how this solution can be applied to solve real-world business challenges."
          />
          <div className="mt-10">
            <CaseStudyPreview data={data.caseConcept} />
          </div>
        </Container>
      </Section>

      {/* Related */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="Keep exploring" title="Related services" />
          <div className="mt-8">
            <RelatedLinks items={relatedServiceLinks(data.relatedServices)} />
          </div>
          <h3 className="mt-14 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Related industries
          </h3>
          <div className="mt-6">
            <RelatedLinks items={relatedIndustryLinks(data.relatedIndustries)} />
          </div>
        </Container>
      </Section>

      <DetailCta origin={{ type: "service", slug: data.slug, label: data.title }} />
    </>
  );
}
